import React, { useEffect, useMemo, useState } from "react";
import {
  AssignmentOutlined,
  CheckCircleOutline,
  HourglassEmptyOutlined,
  PendingActionsOutlined,
  TaskAltOutlined,
} from "@mui/icons-material";
import { Alert, Box, CircularProgress, Typography } from "@mui/material";
import { useNavigate } from "react-router-dom";
import DataTable from "../../../components/common/tables/DataTable";
import { useAuth } from "context/AuthContext";
import api from "services/api";
import { isAdminUser } from "modules/systems/services/application.service";
import { AppYearPicker } from "components/common/inputs";
import SectionCard from "../components/SectionCard";
import StatusChip from "../components/StatusChip";
import { subcontractSummaryService } from "../services/subcontractSummary.service";
import { isCurrentUserApprover } from "../utils/subcontracts/approvalStatus";
import { formatAmount, formatCurrencyWithUnit, formatDate } from "utils/formatters";

const CURRENT_YEAR = new Date().getFullYear();

const normalizeText = (value) =>
  String(value || "")
    .trim()
    .toLocaleLowerCase("vi-VN");

const getField = (item, field) =>
  item?.[field] ?? item?.[`${field.charAt(0).toUpperCase()}${field.slice(1)}`];

const toNumber = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const sumPaymentProfiles = (item) => {
  const profiles = getField(item, "profiles");
  if (!Array.isArray(profiles)) return toNumber(getField(item, "paidAmount"));

  return profiles.reduce(
    (profileTotal, profile) =>
      profileTotal + (profile?.items || []).reduce(
        (itemTotal, paymentItem) => itemTotal + toNumber(getField(paymentItem, "paymentAmount")),
        0,
      ),
    0,
  );
};

const getSignYear = (item) => {
  const signDate = getField(item, "signDate");
  if (!signDate) return null;
  const date = new Date(`${String(signDate).slice(0, 10)}T00:00:00`);
  return Number.isNaN(date.getTime()) ? null : date.getFullYear();
};

const statusEquals = (item, expected) => normalizeText(getField(item, "status")) === normalizeText(expected);
const getSubcontractId = (item) => getField(item, "subcontractId") ?? getField(item, "id");
const getApprovalProfiles = (item) => {
  const profiles = getField(item, "approvalProfiles");
  return Array.isArray(profiles) ? profiles : [];
};
const getAcceptanceApprovalProfiles = (item) => {
  const profiles = getField(item, "acceptanceApprovalProfiles");
  return Array.isArray(profiles) ? profiles : [];
};
const latestBy = (items, fields) =>
  [...items].sort((left, right) => {
    const leftDate = fields.map((field) => getField(left, field)).find(Boolean);
    const rightDate = fields.map((field) => getField(right, field)).find(Boolean);
    return new Date(rightDate || 0) - new Date(leftDate || 0);
  })[0] || null;
const getLatestApprovalStatus = (item) =>
  getField(latestBy(getApprovalProfiles(item), ["submittedAt", "createdAt"]), "status") ||
  getField(item, "approvalStatus") ||
  "Tạo mới";
const isPendingApproval = (item) =>
  getApprovalProfiles(item).some((profile) => normalizeText(getField(profile, "status")) === "chờ phê duyệt");

function Kpi({ label, value, icon, totalValue, paidValue, paidOnly = false }) {
  return (
    <Box className="subcontract-kpi">
      <Box className="subcontract-kpi__top">
        <Box className="subcontract-kpi__title">
          <span className="subcontract-kpi__icon">{icon}</span>
          <Typography>{label}</Typography>
        </Box>
        <Typography className="subcontract-kpi__value">{value}</Typography>
      </Box>
      <Box className={`subcontract-kpi__bottom ${paidOnly ? "single" : ""}`}>
        {!paidOnly && (
          <Box>
            <span>Tổng giá trị</span>
            <strong className="subcontract-kpi__amount subcontract-kpi__amount--contract">
              {formatCurrencyWithUnit(totalValue)}
            </strong>
          </Box>
        )}
        {paidValue !== undefined && (
          <Box>
            <span>Đã thanh toán</span>
            <strong className="subcontract-kpi__amount subcontract-kpi__amount--paid">
              {formatCurrencyWithUnit(paidValue)}
            </strong>
          </Box>
        )}
      </Box>
    </Box>
  );
}

export default function DashboardPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [contracts, setContracts] = useState([]);
  const [yearRange, setYearRange] = useState({ from: CURRENT_YEAR, to: CURRENT_YEAR });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [allowedSubcontractIds, setAllowedSubcontractIds] = useState(null);

  useEffect(() => {
    let active = true;

    const loadAccessibleSubcontracts = async () => {
      if (!user?.userId) {
        if (active) setAllowedSubcontractIds("none");
        return;
      }

      if (isAdminUser(user)) {
        if (active) setAllowedSubcontractIds("all");
        return;
      }

      try {
        const response = await api.get(`/UserSubcontract/user/${user.userId}`, {
          params: { pageSize: 1000, pageNumber: 1 },
        });
        const ids = (response?.data?.data || [])
          .map((item) => item.subcontractId ?? item.SubcontractId)
          .filter((value) => value != null);

        if (active) setAllowedSubcontractIds(ids.length > 0 ? ids : "none");
      } catch (requestError) {
        if (active) {
          setAllowedSubcontractIds("error");
          setError(
            requestError.response?.data?.message ||
              requestError.message ||
              "Không thể tải danh sách phân quyền giao khoán.",
          );
        }
      }
    };

    setContracts([]);
    setError("");
    setLoading(true);
    setAllowedSubcontractIds(null);
    loadAccessibleSubcontracts();

    return () => {
      active = false;
    };
  }, [user]);

  useEffect(() => {
    let active = true;

    if (allowedSubcontractIds === null) {
      return () => {
        active = false;
      };
    }

    if (allowedSubcontractIds === "none" || allowedSubcontractIds === "error") {
      setContracts([]);
      setLoading(false);
      return () => {
        active = false;
      };
    }

    setLoading(true);
    Promise.all([
      subcontractSummaryService.getPaymentSummaries({ subcontractIds: allowedSubcontractIds }),
      subcontractSummaryService.getApprovalSummaries({ subcontractIds: allowedSubcontractIds }),
      subcontractSummaryService.getAcceptanceApprovalSummaries({ subcontractIds: allowedSubcontractIds }),
    ])
      .then(([paymentItems, approvalItems, acceptanceApprovalItems]) => {
        if (!active) return;
        const approvalsBySubcontract = new Map(
          (approvalItems || []).map((item) => [String(getSubcontractId(item)), item]),
        );
        const acceptanceApprovalsBySubcontract = new Map(
          (acceptanceApprovalItems || []).map((item) => [String(getSubcontractId(item)), item]),
        );
        setContracts((paymentItems || []).map((item) => {
          const approvalSummary = approvalsBySubcontract.get(String(getSubcontractId(item)));
          const acceptanceApprovalSummary = acceptanceApprovalsBySubcontract.get(
            String(getSubcontractId(item)),
          );
          return {
            ...item,
            approvalProfiles: approvalSummary?.profiles || [],
            approvalStatus: getLatestApprovalStatus(approvalSummary || item),
            acceptanceApprovalProfiles:
              acceptanceApprovalSummary?.acceptanceApprovalProfiles || [],
          };
        }));
        setError("");
      })
      .catch((requestError) => {
        if (!active) return;
        setContracts([]);
        setError(requestError.response?.data?.message || requestError.message || "Không thể tải dashboard từ backend.");
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => {
      active = false;
    };
  }, [allowedSubcontractIds]);

  const normalizedRange = useMemo(() => {
    const from = Number(yearRange.from) || CURRENT_YEAR;
    const to = Number(yearRange.to) || from;
    return {
      from: Math.min(from, to),
      to: Math.max(from, to),
    };
  }, [yearRange]);

  const filteredContracts = useMemo(
    () => contracts.filter((item) => {
      const year = getSignYear(item);
      return year >= normalizedRange.from && year <= normalizedRange.to;
    }),
    [contracts, normalizedRange],
  );

  const summarize = (items) => ({
    count: items.length,
    totalValue: items.reduce((sum, item) => sum + toNumber(getField(item, "totalValue")), 0),
    paidValue: items.reduce((sum, item) => sum + sumPaymentProfiles(item), 0),
  });

  const stats = useMemo(() => {
    const currentUserId = user?.userId ?? user?.UserId;
    const inProgress = filteredContracts.filter((item) => statusEquals(item, "Đang thực hiện"));
    const completed = filteredContracts.filter((item) => statusEquals(item, "Hoàn thành"));
    const pendingApproval = filteredContracts.filter(isPendingApproval);
    const pendingApprovalItems = pendingApproval.filter((item) =>
      getApprovalProfiles(item).some((profile) => isCurrentUserApprover(profile, currentUserId)),
    );
    const pendingAcceptanceApprovalItems = filteredContracts.flatMap((item) =>
      getAcceptanceApprovalProfiles(item)
        .filter((profile) => isCurrentUserApprover(profile, currentUserId))
        .map((profile) => ({
          ...item,
          acceptanceApprovalProfileId: getField(profile, "acceptanceApprovalProfileId"),
          acceptanceProfileId: getField(profile, "acceptanceProfileId"),
          acceptanceName: getField(profile, "acceptanceName"),
          acceptanceDate: getField(profile, "acceptanceDate"),
          acceptanceApprovalName: getField(profile, "approvalName"),
          acceptanceApprovalSubmittedAt: getField(profile, "submittedAt"),
          acceptanceApprovalStatus: getField(profile, "status"),
        })),
    );

    return {
      total: summarize(filteredContracts),
      inProgress: summarize(inProgress),
      completed: summarize(completed),
      pendingApproval: summarize(pendingApproval),
      pendingApprovalItems,
      pendingAcceptanceApprovalItems,
    };
  }, [filteredContracts, user]);

  const subcontractApprovalColumns = [
    { accessorKey: "subcontractNumber", header: "Số giao khoán", size: 90 },
    {
      accessorKey: "subcontractName",
      header: "Tên quyết định giao khoán",
      size: 220,
      muiTableBodyCellProps: () => ({ align: "left" }),
    },
    { accessorKey: "signDate", header: "Ngày ký", size: 90, Cell: ({ cell }) => formatDate(cell.getValue()) },
    { accessorKey: "status", header: "Trạng thái", size: 90, Cell: ({ cell }) => <StatusChip value={cell.getValue()} /> },
    {
      accessorKey: "approvalStatus",
      header: "Phê duyệt",
      size: 100,
      Cell: ({ row }) => <StatusChip value={getLatestApprovalStatus(row.original)} />,
    },
    { accessorKey: "totalValue", header: "Giá trị", size: 100, Cell: ({ cell }) => formatAmount(cell.getValue()) },
    {
      id: "paidValue",
      header: "Đã thanh toán",
      size: 110,
      accessorFn: (row) => sumPaymentProfiles(row),
      Cell: ({ cell }) => formatAmount(cell.getValue()),
    },
  ];

  const acceptanceApprovalColumns = [
    { accessorKey: "subcontractNumber", header: "Số giao khoán", size: 90 },
    {
      accessorKey: "subcontractName",
      header: "Tên quyết định giao khoán",
      size: 190,
      muiTableBodyCellProps: () => ({ align: "left" }),
    },
    {
      accessorKey: "acceptanceName",
      header: "Tên nghiệm thu",
      size: 180,
      muiTableBodyCellProps: () => ({ align: "left" }),
    },
    {
      accessorKey: "acceptanceDate",
      header: "Ngày nghiệm thu",
      size: 100,
      Cell: ({ cell }) => formatDate(cell.getValue()),
    },
    {
      accessorKey: "acceptanceApprovalName",
      header: "Hồ sơ phê duyệt",
      size: 180,
      muiTableBodyCellProps: () => ({ align: "left" }),
    },
    {
      accessorKey: "acceptanceApprovalSubmittedAt",
      header: "Ngày gửi",
      size: 100,
      Cell: ({ cell }) => formatDate(cell.getValue()),
    },
    {
      accessorKey: "acceptanceApprovalStatus",
      header: "Trạng thái",
      size: 110,
      Cell: ({ cell }) => <StatusChip value={cell.getValue()} />,
    },
  ];

  const handleYearChange = (field, value) => {
    setYearRange((current) => ({ ...current, [field]: Number(value) || CURRENT_YEAR }));
  };

  const handlePendingApprovalRowClick = (item) => {
    const subcontractId = getSubcontractId(item);
    if (subcontractId === null || subcontractId === undefined) return;

    navigate(`/subcontracts/contracts/${encodeURIComponent(subcontractId)}`);
  };

  return (
    <Box className="subcontract-page">
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Box className="subcontract-dashboard-filter">
        <Typography className="subcontract-dashboard-filter__label">Lọc theo năm ký giao khoán</Typography>
        <AppYearPicker label="Từ năm" value={yearRange.from} onChange={(value) => handleYearChange("from", value)} />
        <AppYearPicker label="Đến năm" value={yearRange.to} onChange={(value) => handleYearChange("to", value)} />
      </Box>

      {loading ? (
        <Box className="subcontract-dashboard-loading">
          <CircularProgress size={32} />
        </Box>
      ) : (
        <>
          <Box className="subcontract-kpi-grid">
            <Kpi
              label="Tổng số quyết định giao khoán"
              value={stats.total.count}
              totalValue={stats.total.totalValue}
              icon={<AssignmentOutlined />}
            />
            <Kpi
              label="Đang thực hiện"
              value={stats.inProgress.count}
              totalValue={stats.inProgress.totalValue}
              paidValue={stats.inProgress.paidValue}
              icon={<HourglassEmptyOutlined />}
            />
            <Kpi
              label="Hoàn thành"
              value={stats.completed.count}
              paidValue={stats.completed.paidValue}
              paidOnly
              icon={<TaskAltOutlined />}
            />
            <Kpi
              label="Chưa phê duyệt"
              value={stats.pendingApproval.count}
              totalValue={stats.pendingApproval.totalValue}
              paidValue={stats.pendingApproval.paidValue}
              icon={<PendingActionsOutlined />}
            />
          </Box>

          <SectionCard
            title="Danh sách các giao khoán đang ở trạng thái chờ phê duyệt"
          >
            {stats.pendingApprovalItems.length > 0 ? (
              <DataTable
                columns={subcontractApprovalColumns}
                data={stats.pendingApprovalItems}
                onRowClick={handlePendingApprovalRowClick}
                muiTableBodyRowProps={({ row }) => ({
                  title: `Click để xem chi tiết giao khoán ${row.original.subcontractNumber}`,
                  sx: {
                    cursor: "pointer",
                  },
                })}
              />
            ) : (
              <Alert severity="success" icon={<CheckCircleOutline />}>
                Chưa có giao khoán chờ bạn phê duyệt trong khoảng năm đã chọn.
              </Alert>
            )}
          </SectionCard>

          <SectionCard
            title="Danh sách các nghiệm thu đang ở trạng thái chờ phê duyệt"
            sx={{ mt: 2 }}
          >
            {stats.pendingAcceptanceApprovalItems.length > 0 ? (
              <DataTable
                columns={acceptanceApprovalColumns}
                data={stats.pendingAcceptanceApprovalItems}
                onRowClick={handlePendingApprovalRowClick}
                muiTableBodyRowProps={({ row }) => ({
                  title: `Click để xem chi tiết giao khoán ${row.original.subcontractNumber}`,
                  sx: {
                    cursor: "pointer",
                  },
                })}
              />
            ) : (
              <Alert severity="success" icon={<CheckCircleOutline />}>
                Chưa có nghiệm thu chờ bạn phê duyệt trong khoảng năm đã chọn.
              </Alert>
            )}
          </SectionCard>
        </>
      )}
    </Box>
  );
}
